import { Icon } from '@iconify/react';
import { debounce } from 'es-toolkit';
import React, { useRef, useState } from 'react';

import {
  Box,
  List,
  Avatar,
  Popover,
  Tooltip,
  TextField,
  IconButton,
  Typography,
  ListItemText,
  ListItemAvatar,
  ListItemButton,
} from '@mui/material';

import { formatPhoneNumber } from 'src/utils/format-number';

import { useTranslate } from 'src/locales';
import { truncateText } from 'src/pages/chat/utils/get-message';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';
import { useCallContext } from 'src/pages/chat/hooks/use-call-context';
import { useFilterContactMutation } from 'src/pages/contacts/slice/contactsApi';

import { Iconify } from 'src/components/iconify';
import { useSettingsContext } from 'src/components/settings';

export const DialerPopover = () => {
  const { t } = useTranslate('translation');
  const { registered, makeCall, handleCallUserDetails } = useCallContext();

  const [filterContact] = useFilterContactMutation();
  const [getMediaUrl] = useGetMediaUrlMutation();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [userContactList, setUserContactList] = useState<any[]>([]);

  const listRef = useRef<HTMLUListElement | null>(null);
  const settings = useSettingsContext();

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setPhoneNumber('');
    setUserContactList([]);
  };

  const handleMakeCall = () => {
    if (phoneNumber && !/[a-zA-Z]/.test(phoneNumber)) {
      const updateNumber =
        phoneNumber?.length >= 10 && phoneNumber.startsWith('0')
          ? phoneNumber.replace(/^0/, '966')
          : phoneNumber;

      makeCall(updateNumber, true);
      handleClose();
    } else {
      console.warn('Enter a valid number');
    }
  };

  const getProfileUrl = async (filePath: string | null) => {
    if (filePath) {
      const { data: uploadFileUrl } = await getMediaUrl({ fileName: filePath });
      return uploadFileUrl;
    }
    return '';
  };

  const setResponseFunc = async (res: { list: any[]; total: number }) => {
    const updatedList = await Promise.all(
      res.list.map(async (item) => ({
        ...item,
        phone: item.phone && !item.phone.includes('+') ? `+${item.phone}` : (item.phone ?? ''),
        ProfileURL: await getProfileUrl(item.ProfileURL),
      }))
    );
    setUserContactList(updatedList);
  };

  const debouncedSearch = useRef(
    debounce(async (value: string) => {
      if (value && value.length >= 3) {
        const contactDetails = await filterContact({
          filters: [{ fieldname: 'createdAt', sort: 1 }],
          globalSearch: value,
          combineFieldsGroups: [['firstName', 'lastName']],
          page: 1,
          limit: 5,
        }).unwrap();

        if (contactDetails?.list?.length > 0) {
          await setResponseFunc(contactDetails);
        } else {
          setUserContactList([]);
        }
      } else {
        setUserContactList([]);
      }
    }, 500)
  ).current;

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    let formattedValue = value;
    if (formattedValue?.startsWith('0')) {
      formattedValue = '966' + formattedValue?.slice(1);
    }
    setPhoneNumber(value);
    debouncedSearch(formattedValue);
  };

  const handleUserClick = (user: any) => {
    if (user?.phone) {
      makeCall(user?.phone.replace('+', ''));
      handleCallUserDetails(user);

      handleClose();
    }
  };

  const isCallDisabled = !phoneNumber || /[a-zA-Z]/.test(phoneNumber);

  return (
    <>
      <IconButton onClick={handleOpen} disabled={!registered}>
        <Tooltip title={t('DIALER')}>
          <Icon icon="mage-dialer-keypad" />
        </Tooltip>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            sx: {
              width: 300,
              minWidth: 300,
              maxHeight: '50vh',
              ml: 'auto',
              top: '7% !important',
              backgroundColor: 'background.default',
              direction: settings.state.direction,
              ...(settings.state.direction === 'ltr' ? { left: '40px' } : { right: '40px' }),
              display: 'flex',
              flexDirection: 'column',
            },
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            flexDirection: settings.state.direction === 'rtl' ? 'row-reverse' : 'row',
          }}
        >
          <TextField
            placeholder={t('SEARCH_BY_CONTACT_NAME_OR_NUMBER')}
            variant="outlined"
            size="small"
            value={phoneNumber}
            onChange={handleInputChange}
            sx={{
              width: '78%',
              '& .MuiInputBase-input::placeholder': {
                fontSize: '0.75rem',
              },
            }}
            inputProps={{ dir: settings.state.direction }}
          />
          <Tooltip title={t('CALL')}>
            <span>
              <IconButton onClick={handleMakeCall} disabled={isCallDisabled}>
                <Iconify icon="solar:phone-bold" />
              </IconButton>
            </span>
          </Tooltip>
        </Box>

        {phoneNumber && phoneNumber.length !== 0 && (
          <Box
            sx={{
              px: 1,
              overflowY: 'auto',
              height: `${Math.min(userContactList.length * 60 || 60, 260)}px`,
              transition: 'height 0.3s ease',
            }}
          >
            <List dense ref={listRef} component="ul">
              {userContactList.length > 0 ? (
                userContactList.map((user: any) => (
                  <Box key={user.id}>
                    <ListItemButton
                      sx={{
                        cursor: 'pointer',
                        borderRadius: 1,
                        transition: 'background-color 0.2s',
                        '&:hover': {
                          backgroundColor: 'action.hover',
                        },
                        alignItems: 'center',
                        px: 2,
                        flexDirection: settings.state.direction === 'rtl' ? 'row-reverse' : 'row',
                        textAlign: settings.state.direction === 'rtl' ? 'right' : 'left',
                      }}
                      disabled={!user?.phone || user?.phone?.length === 0}
                    >
                      <ListItemAvatar
                        sx={{
                          mr: settings.state.direction === 'rtl' ? 2 : 1,
                        }}
                      >
                        <Avatar
                          alt={user?.firstName}
                          src={user?.ProfileURL ?? ''}
                          sx={{ width: 36, height: 36 }}
                        />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Tooltip
                            title={
                              user?.firstName && user?.lastName
                                ? `${user.firstName} ${user.lastName}`
                                : user.email || user?.phone || ''
                            }
                          >
                            <span>
                              {truncateText(
                                user?.firstName && user?.lastName
                                  ? `${user.firstName} ${user.lastName}`
                                  : user.email || user?.phone || '',
                                20
                              )}
                            </span>
                          </Tooltip>
                        }
                        secondary={`${formatPhoneNumber(user?.phone) || ''}`}
                        slotProps={{
                          primary: {
                            style: {
                              fontWeight: 500,
                              textAlign: settings.state.direction === 'rtl' ? 'right' : 'left',
                            },
                          },
                          secondary: {
                            style: {
                              textAlign: settings.state.direction === 'rtl' ? 'right' : 'left',
                            },
                          },
                        }}
                      />
                      <Tooltip title={t('CALL')}>
                        <IconButton onClick={() => handleUserClick(user)}>
                          <Iconify icon="solar:phone-bold" />
                        </IconButton>
                      </Tooltip>
                    </ListItemButton>
                  </Box>
                ))
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ px: 2, py: 1, textAlign: 'center' }}
                >
                  {t('NO_USER_FOUND')}
                </Typography>
              )}
            </List>
          </Box>
        )}
      </Popover>
    </>
  );
};
